import { useEffect, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

export const ARCHIVE_STATUS_OPTIONS = [
  { value: "active_only", label: "Active only" },
  { value: "show_archived", label: "Show archived" },
  { value: "archived_only", label: "Archived only" },
];

export function ArchiveStatusFilterPanel({
  value,
  onChange,
  idPrefix = "archive-status",
}) {
  const [selectionState, setSelectionState] = useState("default");

  useEffect(() => {
    if (value === "active_only") {
      setSelectionState((current) =>
        current === "none" ? "none" : "default"
      );
      return;
    }
    setSelectionState(value);
  }, [value]);

  const isOptionChecked = (optionValue) => {
    if (selectionState === "default") {
      return optionValue === "active_only";
    }
    if (selectionState === "none") {
      return false;
    }
    return selectionState === optionValue;
  };

  const handleOptionToggle = (optionValue) => {
    if (isOptionChecked(optionValue)) {
      if (optionValue === "active_only") {
        setSelectionState("none");
      } else {
        setSelectionState("default");
      }
      onChange("active_only");
      return;
    }

    if (optionValue === "active_only") {
      setSelectionState("default");
    } else {
      setSelectionState(optionValue);
    }
    onChange(optionValue);
  };

  return (
    <div className="space-y-3">
      {ARCHIVE_STATUS_OPTIONS.map((option) => (
        <div key={option.value} className="flex items-start gap-3">
          <input
            id={`${idPrefix}-${option.value}`}
            type="checkbox"
            checked={isOptionChecked(option.value)}
            onChange={() => handleOptionToggle(option.value)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label
            htmlFor={`${idPrefix}-${option.value}`}
            className="min-w-0 flex-1 break-words cursor-pointer text-sm font-medium text-gray-900"
          >
            {option.label}
          </label>
        </div>
      ))}
    </div>
  );
}

export default function ArchiveStatusFilter({ value, onChange, className = "" }) {
  const isFilterActive = value !== "active_only";

  return (
    <Popover className={`relative inline-block text-left ${className}`.trim()}>
      <PopoverButton
        className={`group inline-flex items-center justify-center text-sm font-medium ${
          isFilterActive ? "text-indigo-700" : "text-gray-700"
        } hover:text-gray-900`}
      >
        <span>Archive Status</span>
        {isFilterActive ? (
          <span className="ml-1.5 rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-indigo-700">
            1
          </span>
        ) : null}
        <ChevronDownIcon
          aria-hidden="true"
          className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
        />
      </PopoverButton>

      <PopoverPanel
        transition
        className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <ArchiveStatusFilterPanel value={value} onChange={onChange} />
      </PopoverPanel>
    </Popover>
  );
}
